/**
 * HnsContext
 *
 * HNS-game-specific state owned outside GameContext: Marco Polo events,
 * activation/cancel handlers, the three Marco Polo socket listeners, the
 * admin's disqualify and revoke requests, and the results snapshot the server
 * builds for the admin.
 *
 * Mounted by GameTypeProvider when the selected game is a Hide and Seek or
 * Hide and Seek Teams game (Marco Polo and round events are the same for both;
 * the results snapshot is Hide and Seek's only).
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';

import { onGameStateUpdated, useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  Game,
  GameType,
  SocketEvent,
  MarcoPoloActivateRequest,
  MarcoPoloCancelRequest,
  MarcoPoloActivateResponse,
  MarcoPoloActivatedBroadcast,
  isEligibleForMarcoPolo,
  MarcoPoloCancelledBroadcast,
  MarcoPoloResponseBroadcast,
  MarcoPoloEvent,
  MarcoPoloPlayerResponse,
  RoundEvent,
  RoundEventScheduleRequest,
  RoundEventScheduleResponse,
  RoundEventDeleteRequest,
  RoundEventApplyNowRequest,
  RoundEventApplyNowResponse,
  RoundEventListRequest,
  RoundEventListResponse,
  RoundEventScheduledBroadcast,
  RoundEventUpdatedBroadcast,
  RoundEventDeletedBroadcast,
  RoundEventFiredBroadcast,
  isRoundActive,
  AdminGetHnsResultsRequest,
  AdminHnsResultsResponse,
  GameStateUpdatedBroadcast,
  AdminSetCameraCheckRequest,
  AdminSetCameraCheckResponse,
  AdminDisqualifyPlayerRequest,
  AdminRevokeDisqualificationRequest,
  PlayerDisqualifiedResponse,
} from '@swtorbit/shared-socket-events';
import { gameLogger } from '../utils/logger';
import { useGameContext } from './GameContext';

interface HnsContextType {
  marcoPoloEvents: MarcoPoloEvent[];
  activateMarcoPolo: (request: MarcoPoloActivateRequest) => void;
  cancelMarcoPolo: (request: MarcoPoloCancelRequest) => void;
  roundEvents: RoundEvent[];
  scheduleRoundEvent: (request: RoundEventScheduleRequest) => void;
  deleteRoundEvent: (request: RoundEventDeleteRequest) => void;
  applyRoundEventNow: (request: RoundEventApplyNowRequest) => void;
  hnsAdminResults: AdminHnsResultsResponse | null;
  refreshHnsAdminResults: () => void;
  /** Switch a player's Max Camera Distance check off (or on again) for a round. */
  setCameraCheck: (request: AdminSetCameraCheckRequest) => void;
  /** Disqualify a player from the round in play. Resolves with the server's error, or null. */
  disqualifyPlayer: (request: AdminDisqualifyPlayerRequest) => Promise<string | null>;
  /** Lift a player's disqualification (a round that has ended is scored again). Resolves with the server's error, or null. */
  revokeDisqualification: (request: AdminRevokeDisqualificationRequest) => Promise<string | null>;
}

const HnsContext = createContext<HnsContextType | undefined>(undefined);

interface HnsProviderProps {
  children: ReactNode;
}

export function HnsProvider({ children }: HnsProviderProps) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();
  const [marcoPoloEvents, setMarcoPoloEvents] = useState<MarcoPoloEvent[]>([]);
  const [roundEvents, setRoundEvents] = useState<RoundEvent[]>([]);
  const [hnsAdminResults, setHnsAdminResults] = useState<AdminHnsResultsResponse | null>(null);
  const hnsResultsRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedGameRef = useRef<Game | null>(selectedGame);
  useEffect(() => {
    selectedGameRef.current = selectedGame;
  }, [selectedGame]);

  // Clear Marco Polo events and results when switching games
  useEffect(() => {
    setMarcoPoloEvents([]);
    setRoundEvents([]);
    setHnsAdminResults(null);
  }, [selectedGame?.dbId]);

  // Clear Marco Polo events when the active round changes — prevents previous
  // round's events from appearing in the next round's card.
  const activeRoundId = useMemo(
    () => selectedGame?.rounds.find(r => r.status === 'hiding' || r.status === 'seeking')?.id,
    [selectedGame]
  );
  useEffect(() => {
    setMarcoPoloEvents([]);
    setRoundEvents([]);
  }, [activeRoundId]);

  const activateMarcoPolo = useCallback((request: MarcoPoloActivateRequest) => {
    if (!socket) return;
    socket.emit(SocketEvent.MARCO_POLO_ACTIVATE, request);
  }, [socket]);

  const cancelMarcoPolo = useCallback((request: MarcoPoloCancelRequest) => {
    if (!socket) return;
    socket.emit(SocketEvent.MARCO_POLO_CANCEL, request);
  }, [socket]);

  const scheduleRoundEvent = useCallback((request: RoundEventScheduleRequest) => {
    if (!socket) return;
    socket.emit(SocketEvent.ROUND_EVENT_SCHEDULE, request, (res: RoundEventScheduleResponse) => {
      if (!res?.success) {
        gameLogger.warn('Round event schedule failed', { error: res?.error });
      }
    });
  }, [socket]);

  const setCameraCheck = useCallback((request: AdminSetCameraCheckRequest) => {
    if (!socket) return;
    socket.once(SocketEvent.ADMIN_SET_CAMERA_CHECK, (res: AdminSetCameraCheckResponse) => {
      if (!res?.success) gameLogger.warn('Camera check change failed', { error: res?.error });
    });
    socket.emit(SocketEvent.ADMIN_SET_CAMERA_CHECK, request);
  }, [socket]);

  /** Send an admin disqualification request; the server answers on the same event. */
  const sendDisqualificationRequest = useCallback(
    (event: SocketEvent, request: AdminDisqualifyPlayerRequest | AdminRevokeDisqualificationRequest): Promise<string | null> => {
      if (!socket) return Promise.resolve('Not connected to the server');
      return new Promise((resolve) => {
        socket.once(event, (res: PlayerDisqualifiedResponse) => {
          resolve(res?.success ? null : res?.error ?? 'Something went wrong');
        });
        socket.emit(event, request);
      });
    },
    [socket],
  );

  const disqualifyPlayer = useCallback(
    (request: AdminDisqualifyPlayerRequest) => sendDisqualificationRequest(SocketEvent.ADMIN_DISQUALIFY_PLAYER, request),
    [sendDisqualificationRequest],
  );

  const revokeDisqualification = useCallback(
    (request: AdminRevokeDisqualificationRequest) => sendDisqualificationRequest(SocketEvent.ADMIN_REVOKE_DISQUALIFICATION, request),
    [sendDisqualificationRequest],
  );

  const deleteRoundEvent = useCallback((request: RoundEventDeleteRequest) => {
    if (!socket) return;
    socket.emit(SocketEvent.ROUND_EVENT_DELETE, request);
  }, [socket]);

  const applyRoundEventNow = useCallback((request: RoundEventApplyNowRequest) => {
    if (!socket) return;
    socket.emit(SocketEvent.ROUND_EVENT_APPLY_NOW, request, (res: RoundEventApplyNowResponse) => {
      if (!res?.success) {
        gameLogger.warn('Round event apply-now failed', { error: res?.error });
      }
    });
  }, [socket]);

  const refreshHnsAdminResults = useCallback(() => {
    const game = selectedGameRef.current;
    // Hide and Seek Teams games share this provider (Marco Polo, round events)
    // but have their own results (HnstContext).
    if (!game || !socket || game.gameType !== GameType.HIDE_AND_SEEK) return;
    const req: AdminGetHnsResultsRequest = { gameId: game.dbId };
    socket.emit(SocketEvent.HNS_ADMIN_GET_RESULTS, req);
  }, [socket]);

  const scheduleHnsAdminResultsRefresh = useCallback(() => {
    if (hnsResultsRefreshTimer.current) {
      clearTimeout(hnsResultsRefreshTimer.current);
    }
    hnsResultsRefreshTimer.current = setTimeout(() => {
      hnsResultsRefreshTimer.current = null;
      refreshHnsAdminResults();
    }, 250);
  }, [refreshHnsAdminResults]);

  // Results snapshot: answered on this socket alone. Captures, disqualifications,
  // Marco Polo answers and round changes all rebroadcast the game, so a live
  // game refetches after each; an archived game's results never change.
  useEffect(() => {
    if (!socket) return;

    const handleAdminHnsResults = (response: AdminHnsResultsResponse) => {
      if (response.gameId !== selectedGameRef.current?.dbId) return;
      setHnsAdminResults(response);
    };

    const handleGameStateUpdated = (broadcast: GameStateUpdatedBroadcast) => {
      const game = selectedGameRef.current;
      if (!game || game.archived || broadcast.game.id !== game.id) return;
      scheduleHnsAdminResultsRefresh();
    };

    const handleRoundEventFired = (broadcast: RoundEventFiredBroadcast) => {
      const game = selectedGameRef.current;
      if (!game || game.archived || broadcast.roomCode !== game.roomCode) return;
      scheduleHnsAdminResultsRefresh();
    };

    socket.on(SocketEvent.HNS_ADMIN_RESULTS, handleAdminHnsResults);
    const offGameState = onGameStateUpdated(socket, handleGameStateUpdated);
    socket.on(SocketEvent.ROUND_EVENT_FIRED, handleRoundEventFired);

    return () => {
      socket.off(SocketEvent.HNS_ADMIN_RESULTS, handleAdminHnsResults);
      offGameState();
      socket.off(SocketEvent.ROUND_EVENT_FIRED, handleRoundEventFired);
      if (hnsResultsRefreshTimer.current) {
        clearTimeout(hnsResultsRefreshTimer.current);
        hnsResultsRefreshTimer.current = null;
      }
    };
  }, [socket, scheduleHnsAdminResultsRefresh]);

  useEffect(() => {
    if (!socket) return;

    // MARCO_POLO_ACTIVATED carries both this admin's own activation ack (with
    // the full event) and the room broadcast every activation sends — including
    // scheduled ones, which have no ack. Take either; the ack's exact target
    // list wins over the one derived from the round.
    const handleMarcoPoloActivated = (payload: MarcoPoloActivateResponse | MarcoPoloActivatedBroadcast) => {
      let event: MarcoPoloEvent | null = null;
      let fromAck = false;
      if ('eventId' in payload) {
        const game = selectedGameRef.current;
        if (payload.roomCode !== game?.roomCode) return;
        const round = game.rounds.find(r => isRoundActive(r));
        event = {
          id: payload.eventId,
          activatedAt: payload.activatedAt,
          status: 'active',
          targetPlayerIds: round ? round.players.filter(isEligibleForMarcoPolo).map(p => p.playerId) : [],
          responses: [],
        };
      } else if (payload.success && payload.event) {
        event = payload.event;
        fromAck = true;
      }
      if (!event) return;
      const activated = event;
      gameLogger.debug('Marco Polo activated', { eventId: activated.id });
      setMarcoPoloEvents(prev => {
        const existing = prev.find(e => e.id === activated.id);
        if (!existing) return [...prev, activated];
        if (!fromAck) return prev;
        return prev.map(e => (e.id === activated.id ? { ...e, targetPlayerIds: activated.targetPlayerIds } : e));
      });
    };

    const handleMarcoPoloCancelled = (broadcast: MarcoPoloCancelledBroadcast) => {
      gameLogger.debug('Marco Polo cancelled', { cancelledEventIds: broadcast.cancelledEventIds });
      if (broadcast.roomCode === selectedGameRef.current?.roomCode) {
        setMarcoPoloEvents(prev => prev.map(e =>
          broadcast.cancelledEventIds.includes(e.id)
            ? { ...e, status: 'cancelled' as const, endedAt: Date.now() }
            : e
        ));
      }
    };

    const handleMarcoPoloResponse = (broadcast: MarcoPoloResponseBroadcast) => {
      gameLogger.debug('Marco Polo response from', { playerId: broadcast.playerId });
      if (broadcast.roomCode === selectedGameRef.current?.roomCode) {
        setMarcoPoloEvents(prev => prev.map(event => {
          const targetIds = event.targetPlayerIds || [];
          if (event.status === 'active' && targetIds.includes(broadcast.playerId)) {
            const newResponse: MarcoPoloPlayerResponse = {
              playerId: broadcast.playerId,
              characterName: broadcast.characterName,
              // Admins are always sent the exact position.
              coordinates: broadcast.coordinates ?? { x: broadcast.approxPosition?.x ?? 0, y: broadcast.approxPosition?.y ?? 0, z: 0 },
              source: broadcast.source,
              responseTimeSeconds: broadcast.responseTimeSeconds,
              penaltySeconds: broadcast.penaltySeconds,
              respondedAt: Date.now(),
            };
            const updatedResponses = [...event.responses, newResponse];
            const allResponded = targetIds.every(
              id => updatedResponses.some(r => r.playerId === id)
            );
            return {
              ...event,
              responses: updatedResponses,
              status: allResponded ? 'completed' as const : event.status,
              endedAt: allResponded ? Date.now() : event.endedAt,
            };
          }
          return event;
        }));
      }
    };

    socket.on(SocketEvent.MARCO_POLO_ACTIVATED, handleMarcoPoloActivated);
    socket.on(SocketEvent.MARCO_POLO_CANCELLED, handleMarcoPoloCancelled);
    socket.on(SocketEvent.MARCO_POLO_RESPONSE_BROADCAST, handleMarcoPoloResponse);

    return () => {
      socket.off(SocketEvent.MARCO_POLO_ACTIVATED, handleMarcoPoloActivated);
      socket.off(SocketEvent.MARCO_POLO_CANCELLED, handleMarcoPoloCancelled);
      socket.off(SocketEvent.MARCO_POLO_RESPONSE_BROADCAST, handleMarcoPoloResponse);
    };
  }, [socket]);

  // Round Events (Ruleset Change + Marco Polo timeline) subscription
  useEffect(() => {
    if (!socket) return;

    const inSelectedGame = (roomCode: string) => roomCode === selectedGameRef.current?.roomCode;

    const handleScheduled = (b: RoundEventScheduledBroadcast) => {
      if (!inSelectedGame(b.roomCode)) return;
      setRoundEvents(prev => (
        prev.some(e => e.id === b.event.id) ? prev : [...prev, b.event]
      ));
    };
    const handleUpdated = (b: RoundEventUpdatedBroadcast) => {
      if (!inSelectedGame(b.roomCode)) return;
      setRoundEvents(prev => prev.map(e => e.id === b.event.id ? b.event : e));
    };
    const handleDeleted = (b: RoundEventDeletedBroadcast) => {
      if (!inSelectedGame(b.roomCode)) return;
      setRoundEvents(prev => prev.filter(e => e.id !== b.eventId));
    };
    const handleFired = (b: RoundEventFiredBroadcast) => {
      if (!inSelectedGame(b.roomCode)) return;
      setRoundEvents(prev => {
        if (prev.some(e => e.id === b.event.id)) {
          return prev.map(e => e.id === b.event.id ? b.event : e);
        }
        return [...prev, b.event];
      });
    };

    socket.on(SocketEvent.ROUND_EVENT_SCHEDULED, handleScheduled);
    socket.on(SocketEvent.ROUND_EVENT_UPDATED, handleUpdated);
    socket.on(SocketEvent.ROUND_EVENT_DELETED, handleDeleted);
    socket.on(SocketEvent.ROUND_EVENT_FIRED, handleFired);

    return () => {
      socket.off(SocketEvent.ROUND_EVENT_SCHEDULED, handleScheduled);
      socket.off(SocketEvent.ROUND_EVENT_UPDATED, handleUpdated);
      socket.off(SocketEvent.ROUND_EVENT_DELETED, handleDeleted);
      socket.off(SocketEvent.ROUND_EVENT_FIRED, handleFired);
    };
  }, [socket]);

  // Refresh round events whenever the active round changes
  useEffect(() => {
    if (!socket) return;
    const game = selectedGame;
    const active = game?.rounds.find(r => isRoundActive(r));
    if (!game || !active) {
      setRoundEvents([]);
      return;
    }
    const req: RoundEventListRequest = {
      roomCode: game.roomCode,
      roundIndex: game.rounds.indexOf(active),
    };
    socket.emit(SocketEvent.ROUND_EVENT_LIST, req, (res: RoundEventListResponse) => {
      if (res?.success && res.events) {
        setRoundEvents(res.events);
      }
    });
  }, [socket, selectedGame]);

  const value = useMemo(() => ({
    marcoPoloEvents,
    activateMarcoPolo,
    cancelMarcoPolo,
    roundEvents,
    scheduleRoundEvent,
    deleteRoundEvent,
    applyRoundEventNow,
    hnsAdminResults,
    refreshHnsAdminResults,
    setCameraCheck,
    disqualifyPlayer,
    revokeDisqualification,
  }), [
    marcoPoloEvents, activateMarcoPolo, cancelMarcoPolo,
    roundEvents, scheduleRoundEvent, deleteRoundEvent, applyRoundEventNow,
    hnsAdminResults, refreshHnsAdminResults, setCameraCheck,
    disqualifyPlayer, revokeDisqualification,
  ]);

  return <HnsContext.Provider value={value}>{children}</HnsContext.Provider>;
}

export function useHnsContext() {
  const context = useContext(HnsContext);
  if (context === undefined) {
    throw new Error('useHnsContext must be used within an HnsProvider');
  }
  return context;
}
